<template>
  <v-container fluid class="pa-2">
    <v-row>
      <v-col>
        <div v-if="hasDetails">
          <div v-if="type" class="ml-13 text-size">
            {{ type }}
          </div>
          <h1 class="icon-title-container">
            <AssetIcon
              v-if="iconSrc && iconColor"
              :icon="iconSrc"
              :color="iconColor"
            ></AssetIcon>
            <div class="title-header">{{ title }}</div>
            <div class="clearfix"></div>
          </h1>
          <div
            v-if="details.country && details.country.name"
            class="ml-13 mb-2 text-size"
          >
            {{ details.country.name }}
          </div>
          <Loader v-if="!hasDetails" text="" color="white"></Loader>
          <div v-if="monitorRadius" class="mb-3 monitor-style">
            <span class="application-heading">Monitoring Proximity:</span>
            {{ monitorRadius }}
          </div>
          <div class="mt-5 mb-5">
            <v-btn
              v-if="isAssetToSearchFeatureUnlocked"
              outlined
              small
              @click="handleClickSearchProximity"
            >
              Asset Search
            </v-btn>
            <AssetReportButton
              class="mx-2"
              :id="id"
              :units="unit"
              loader-color="white"
            ></AssetReportButton>
            <v-btn outlined small @click="handleClickOpenAssetPanel">
              Open Asset Table
            </v-btn>
          </div>
          <div>
            <v-toolbar flat :height="44" class="layout-toolbar">
              <v-tabs
                v-model="activeTab"
                background-color="transparent"
                height="40"
                class="layout-tabs"
              >
                <v-tab
                  v-for="tab in headlineTabs"
                  :key="tab.key"
                  class="px-0 tab-text"
                >
                  {{ tab.label }}
                </v-tab>
              </v-tabs>
            </v-toolbar>

            <v-tabs-items dark v-model="activeTab">
              <v-tab-item
                v-if="isAssetsProximityPaneVETabUnlocked"
                key="verified_events"
              >
                <div v-if="hasDetails">
                  <AssetsPaneVerifiedEvents
                    :asset-details="details"
                    :asset-date-range="dateRange"
                  ></AssetsPaneVerifiedEvents>
                </div>
              </v-tab-item>
              <v-tab-item key="ratings">
                <AssetsPaneRatings :country-code="country.countryAlpha2Code">
                </AssetsPaneRatings>
              </v-tab-item>
              <v-tab-item key="asset_information">
                <div v-if="hasDetails">
                  <AssetsPaneDetails :asset-details="details">
                  </AssetsPaneDetails>
                </div>
              </v-tab-item>
            </v-tabs-items>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import lodashGet from "lodash/get";
import lodashCloneDeep from "lodash/cloneDeep";
import { circle } from "@turf/turf";

import { mapGetters } from "vuex";
import {
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_USER,
  MODULE_PREFIX_MAP,
} from "@/store";
import { formatDisplayMonitorRadius } from "@/common/basic.utilities";
import { getDefaultPolygonOptions } from "@/common/maps/styles";
import { mapPolygonData, removeMapData } from "@/common/maps/features";
import { GET_SELECTOR_PANEL, GET_DETAILS_SIDEBAR } from "@/store/actions.type";
import { LABELS } from "@/common/constants/labels";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { getAssetById } from "@/services/assetManagement/assets.service";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";
import { getAssetReport } from "@/services/reporting/reporting.service";

const PANEL_TABS = [
  { key: "verified_events", label: LABELS.verified_events },
  { key: "ratings", label: LABELS.ratings },
  { key: "asset_information", label: LABELS.asset_information },
];

export default {
  name: "AssetsPane",
  components: {
    AssetReportButton: () =>
      import("@/components/reporting/common/AssetReportButton"),
    Loader: () => import("@/components/common/Loader"),
    AssetIcon: () => import("@/components/assetManagement/AssetIcon"),
    AssetsPaneRatings: () =>
      import("@/components/assetManagement/AssetsPaneRatings"),
    AssetsPaneVerifiedEvents: () =>
      import("@/components/assetManagement/AssetsPaneVerifiedEvents"),
    AssetsPaneDetails: () =>
      import("@/components/assetManagement/AssetsPaneDetails"),
  },
  data() {
    return {
      activeTab: PANEL_TABS[1],
      details: {},
      id: 0,
      dateRange: null,
    };
  },
  computed: {
    ...mapGetters({
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
      preferences: MODULE_PREFIX_USER + "preferences",
      maps: MODULE_PREFIX_MAP + "maps",
      selectorPanel: MODULE_PREFIX_HOME + "selectorPanel",
    }),
    isAssetToSearchFeatureUnlocked() {
      return this.featureUnlocked("assets-table-to-search");
    },
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    hasDetails() {
      return Object.keys(this.details).length > 0;
    },
    unit() {
      return this.preferences.poisDistUnits === "miles" ? "mi" : "km";
    },
    title() {
      if (this.details?.title) {
        return this.details.title;
      }
      return null;
    },
    monitorRadius() {
      if (this.details?.monitorRadius) {
        return `${formatDisplayMonitorRadius(
          this.details.monitorRadius,
          this.preferences?.poisDistUnits
        )} ${this.unit}`;
      }
      return null;
    },
    country() {
      return this.details?.country;
    },
    type() {
      return this.details?.assetType?.title;
    },
    iconColor() {
      return this.details?.assetType?.color;
    },
    iconSrc() {
      return this.details?.assetType?.icon;
    },
    isAssetsProximityPaneVETabUnlocked() {
      return this.featureUnlocked("assets-proximity-pane-ve-tab");
    },
    headlineTabs() {
      return !this.isAssetsProximityPaneVETabUnlocked
        ? PANEL_TABS.filter((tab) => tab.key !== "verified_events")
        : PANEL_TABS;
    },
  },
  async mounted() {
    if (
      this.detailsSidebar.selected === DETAIL_SIDEBAR_TYPES.assets &&
      lodashGet(this.detailsSidebar, "features[0].properties.gid")
    ) {
      this.id = this.detailsSidebar.features[0].properties.gid;
      this.dateRange = this.detailsSidebar.features[0].properties.dateRange;
      await this.getDetails();
    }
  },
  methods: {
    async getDetails() {
      this.details = await getAssetById(this.id);
      this.showAssetMonitoringProximity();
    },
    getCustomFieldLabel(label) {
      return label ? upperCaseFirstLetter(label) : label;
    },
    showAssetMonitoringProximity() {
      if (
        this.details?.longitude &&
        this.details?.latitude &&
        this.details?.monitorRadius
      ) {
        const assetProximityFeature = circle(
          [this.details.longitude, this.details.latitude],
          this.details.monitorRadius,
          {
            steps: 64,
            units: "kilometers",
          }
        );

        const assetProximitySource = {
          type: "FeatureCollection",
          features: [assetProximityFeature],
        };

        const assetProximityParams = {
          id: "asset-proximity",
          opacity: 0.3,
        };

        mapPolygonData({
          id: assetProximityParams.id,
          data: assetProximitySource,
          polygonOptions: getDefaultPolygonOptions(assetProximityParams),
          map: this.map,
        });
      }
    },
    hideAssetMonitoringProximity() {
      removeMapData(this.map, "asset-proximity");
    },
    async generateReport(item) {
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.assets,
        EVENT_ACTIONS.clicked,
        `Asset Generate Asset Report (PDF) Click --- ${item.id}`
      );
      const requestTimestamp = new Date().toISOString();
      this.isAssetReportLoading = true;
      await getAssetReport(
        {
          assetId: item.id,
          monitorRadiusUnit: this.unit,
        },
        requestTimestamp
      );
      this.isAssetReportLoading = false;
    },
    handleClickSearchProximity() {
      const selectorPanel = lodashCloneDeep(this.selectorPanel);
      selectorPanel.selected = "search";
      selectorPanel.visible = true;
      selectorPanel.isSelected = true;
      selectorPanel.searchWithAssetRadius = this.details;
      this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_SELECTOR_PANEL,
        selectorPanel
      );

      this.detailsSidebar.visible = false;
      return this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
        this.detailsSidebar
      );
    },
    handleClickOpenAssetPanel() {
      const selectorPanel = lodashCloneDeep(this.selectorPanel);
      selectorPanel.selected = "assets";
      selectorPanel.visible = true;
      selectorPanel.isSelected = true;
      this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_SELECTOR_PANEL,
        selectorPanel
      );

      this.detailsSidebar.visible = false;
      return this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
        this.detailsSidebar
      );
    },
  },
  beforeDestroy() {
    this.details = null;
    this.id = null;
    this.dateRange = null;
    this.hideAssetMonitoringProximity();
  },
};
</script>

<style scoped>
.icon-title-container {
  display: flex;
  align-items: center; /* Vertically centers the text with the icon */
}
.icon-title-container .title {
  margin-left: 8px; /* Adjust the space between the icon and the text */
  white-space: normal;
  word-wrap: break-word;
  flex: 1;
}
.layout-toolbar .v-toolbar__content {
  padding-left: 12px !important;
  padding-right: 1px !important;
}
.layout-tabs .v-tab {
  padding-left: 18px !important;
  padding-right: 18px !important;
}
.tab-text {
  font-size: 14px !important;
}
.title-header {
  font-size: 24px;
  font-weight: bold;
}
.text-size {
  font-size: 13px;
}
.monitor-style {
  font-weight: bold;
}
</style>
